import { useMemo } from 'react';

import { isValidSubEnvironment, type SubEnvironment } from '@change-corgi/config/environments';
import { useUtilityContext } from '@change-corgi/core/react/utilityContext';
import { getWindow, getWindowSafe } from '@change-corgi/core/window';

import type { TypesenseConfig } from 'config/typesense';
import { decorateForDevProxying, getTypesenseConfig } from 'config/typesense';

const PERSONALIZED_DEMO_ENVIRONMENT_TARGET_KEY = 'personalized-demo-environment-target';

export function useTypesenseConfig(): { config: TypesenseConfig; namespace?: string } {
	const {
		environment: { getApiSubEnvironment, getApiEnvironment, getEnvironment },
	} = useUtilityContext();
	const apiEnvironment = getApiEnvironment();
	const apiSubEnvironment = getApiSubEnvironment();

	return useMemo(() => {
		const { config, namespace } = isValidSubEnvironment(apiSubEnvironment)
			? {
					config: getTypesenseConfig(apiSubEnvironment),
					namespace: apiEnvironment === 'demo' ? apiSubEnvironment : undefined,
				}
			: getTypesenseConfigForPersonalizedDemoEnvironment(apiSubEnvironment);
		const isDevProxying = getEnvironment() === 'development' && apiSubEnvironment !== 'development';
		if (isDevProxying) {
			return {
				config: decorateForDevProxying(config, getWindow().location),
				namespace,
			};
		}
		return { config, namespace };
	}, [apiSubEnvironment, apiEnvironment, getEnvironment]);
}

/**
 * This is a bit of a hack to support personalized demo environments which don't have a Typesense server.
 *
 * Since we don't know the target environment of personalized demo environments, we prompt the user to provide it.
 * We save the target environment in localStorage so that we don't prompt the user again.
 * We also alert the user that the target environment is saved in localStorage so that they can clear it to reset.
 */
function getTypesenseConfigForPersonalizedDemoEnvironment(env: string): { config: TypesenseConfig; namespace: string } {
	if (!getWindowSafe()) {
		throw new Error('Window is not available');
	}
	const localStorageValue = localStorage.getItem(PERSONALIZED_DEMO_ENVIRONMENT_TARGET_KEY);
	if (localStorageValue) {
		return { config: getTypesenseConfig(localStorageValue as SubEnvironment), namespace: localStorageValue };
	}

	// eslint-disable-next-line no-alert
	const targetEnvironment = prompt(
		`Looks like you are in a personalized demo environment (${env}). What demo environment are you targeting?`,
	);
	if (!targetEnvironment) {
		throw new Error('No target environment provided');
	}
	localStorage.setItem(PERSONALIZED_DEMO_ENVIRONMENT_TARGET_KEY, targetEnvironment);

	// eslint-disable-next-line no-alert
	window.alert(
		`Personalized demo environment target saved as ${PERSONALIZED_DEMO_ENVIRONMENT_TARGET_KEY} in localStorage - clear it to reset`,
	);
	return { config: getTypesenseConfig(targetEnvironment as SubEnvironment), namespace: targetEnvironment };
}
